import {
  FloatingFormItem,
  InputDate,
  InputDecimal,
  InputText,
} from '@/components/_common';
import { useAccountReceivableSplitInstallment } from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import { AccountRecivableSplitInstallmentFormValues } from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { handleInstallmentAlreadyEdited } from '@/core/domain/financial/AccountReceivable/AccountRecivable.errors';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import { UseDisclosureActions } from '@/hooks/useDisclosure';
import { useAccountReceivableContext } from '@/pages/FinancialPages/BillToReceivePage/ReleasesPage/ReleasesDetailPage';
import { DateTimeFormat } from '@/utils/moment';
import { Button, Col, Form, Modal, Row } from 'antd';
import { AxiosError } from 'axios';
import moment from 'moment';
import { useEffect } from 'react';

interface Props {
  handleChangeAndOpenModal: (
    actions: UseDisclosureActions,
    loading: string,
  ) => void;
  loading?: boolean;
}

const AccountReceivableSplitInstallment: React.FC<Props> = ({
  handleChangeAndOpenModal,
  loading,
}) => {
  const [form] = Form.useForm<AccountRecivableSplitInstallmentFormValues>();
  const { accountReceivable, currentInstallment } =
    useAccountReceivableContext();
  const [modalOpened, modalAction] = useDisclosure(false);

  const splitInstallment = useAccountReceivableSplitInstallment();
  const ruleRequired = useDefaultEmptyFieldError();

  useEffect(() => {
    const installment =
      accountReceivable?.installmentReceivables[currentInstallment];

    form.setFieldsValue({
      fromThatDate: moment(),
      quantityInstallments: 1,
      intervalDays: 30,
      valueTotal:
        (installment?.installmentValue || 0) +
        (installment?.discountIncrease || 0),
    });
  }, [accountReceivable, currentInstallment, modalOpened]);

  async function handleSubmit() {
    try {
      const values = await form.validateFields();

      if (
        accountReceivable &&
        accountReceivable.installmentReceivables[currentInstallment]
      ) {
        const installment =
          accountReceivable.installmentReceivables[currentInstallment];

        await splitInstallment.mutateAsync({
          ...values,
          fromThatDate: values.fromThatDate.format(DateTimeFormat),
          quantityInstallments: Number(values.quantityInstallments),
          accountReceiveId: accountReceivable.id,
          installmentReceiveId: installment.id,
          currentSituation:
            accountReceivable?.installmentReceivables[currentInstallment]
              .situation,
        });
      }
    } catch (err: unknown) {
      const error = err as AxiosError;

      if (error.response?.data) {
        if (handleInstallmentAlreadyEdited(error)) {
          modalAction.close();
        }
      }
    }
  }

  function handleCancel() {
    form.resetFields();
    modalAction.close();
  }

  return (
    <>
      <Button
        block
        type="primary"
        loading={loading}
        onClick={() => handleChangeAndOpenModal(modalAction, 'parcel')}
      >
        Parcelamento
      </Button>

      <Modal
        title="Parcelamento"
        onOk={handleSubmit}
        onCancel={handleCancel}
        open={modalOpened}
        confirmLoading={splitInstallment.isLoading}
        okText="Parcelar"
        width={860}
      >
        <p>
          Informe os dados abaixo para criar múltiplas parcelas com base no
          valor da conta.
        </p>
        <Form form={form}>
          <Row gutter={[24, 24]}>
            <Col span={24} sm={12} lg={6}>
              <FloatingFormItem name="valueTotal" label="Valor">
                <InputDecimal disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} lg={6}>
              <FloatingFormItem
                name="fromThatDate"
                label="A partir de"
                rules={[ruleRequired]}
              >
                <InputDate />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} lg={6}>
              <FloatingFormItem
                name="quantityInstallments"
                label="Parcelas"
                rules={[ruleRequired]}
              >
                <InputText onlyNumber />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} lg={6}>
              <FloatingFormItem
                name="intervalDays"
                label="Intervalo (dias)"
                rules={[ruleRequired]}
              >
                <InputText onlyNumber />
              </FloatingFormItem>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default AccountReceivableSplitInstallment;
